
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import ModernButton from '@/components/dashboard/ModernButton';
import { ModelObject } from '@/hooks/useLLMProviders';

interface AddModelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onModelAdded: (
    providerId: number,
    modelName: string,
    displayName: string,
    costPer1kInputTokens: number | null,
    costPer1kOutputTokens: number | null,
    isPremium: boolean
  ) => Promise<void>;
  onModelUpdated?: (
    modelId: number,
    providerId: number,
    modelName: string,
    displayName: string,
    costPer1MInputTokens: number | null,
    costPer1MOutputTokens: number | null,
    isPremium: boolean
  ) => Promise<void>;
  providerId: number;
  providerName: string;
  editingModel?: ModelObject | null;
}

const AddModelDialog = ({
  isOpen,
  onClose,
  onModelAdded,
  onModelUpdated,
  providerId,
  providerName,
  editingModel = null
}: AddModelDialogProps) => {
  const [modelName, setModelName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [costPer1MInputTokens, setCostPer1MInputTokens] = useState('');
  const [costPer1MOutputTokens, setCostPer1MOutputTokens] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = Boolean(editingModel);

  const resetForm = () => {
    setModelName('');
    setDisplayName('');
    setCostPer1MInputTokens('');
    setCostPer1MOutputTokens('');
    setIsPremium(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (editingModel) {
      setModelName(editingModel.name || '');
      setDisplayName(editingModel.display_name || editingModel.name || '');
      setCostPer1MInputTokens(
        editingModel.cost_per_1m_input_tokens !== null && editingModel.cost_per_1m_input_tokens !== undefined
          ? String(editingModel.cost_per_1m_input_tokens)
          : ''
      );
      setCostPer1MOutputTokens(
        editingModel.cost_per_1m_output_tokens !== null && editingModel.cost_per_1m_output_tokens !== undefined
          ? String(editingModel.cost_per_1m_output_tokens)
          : ''
      );
      setIsPremium(Boolean(editingModel.is_premium));
    } else {
      setModelName('');
      setDisplayName('');
      setCostPer1MInputTokens('');
      setCostPer1MOutputTokens('');
      setIsPremium(false);
    }
  }, [editingModel, isOpen]);

  const parseCost = (value: string) => {
    if (value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!modelName.trim()) return;

    try {
      setIsLoading(true);
      const inputCostValue = parseCost(costPer1MInputTokens);
      const outputCostValue = parseCost(costPer1MOutputTokens);

      if (isEditMode && editingModel && onModelUpdated) {
        await onModelUpdated(
          editingModel.id,
          providerId,
          modelName.trim(),
          displayName.trim(),
          inputCostValue,
          outputCostValue,
          isPremium
        );
      } else {
        await onModelAdded(
          providerId,
          modelName.trim(),
          displayName.trim(),
          inputCostValue,
          outputCostValue,
          isPremium
        );
      }
      resetForm();
      onClose();
    } catch (error) {
      // Error is handled in the hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? `Edit ${editingModel?.display_name || editingModel?.name || 'Model'}` : `Add Model to ${providerName}`}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the configuration for this model.'
              : 'Add a new model to this provider configuration.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="modelName">Model Name</Label>
            <Input
              id="modelName"
              placeholder="e.g., gpt-4o, claude-3-sonnet"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              disabled={isLoading}
            />
            <Label htmlFor="displayName">Display Name (Semantic name)</Label>
            <Input
              id="displayName"
              placeholder="e.g., Open AI GPT Turbo"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isLoading}
            />
            <Label htmlFor="inputCost">Cost per 1M Input Tokens</Label>
            <Input
              id="inputCost"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="e.g., 2.50"
              value={costPer1MInputTokens}
              onChange={(e) => setCostPer1MInputTokens(e.target.value)}
              disabled={isLoading}
            />
            <Label htmlFor="outputCost">Cost per 1M Output Tokens</Label>
            <Input
              id="outputCost"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="e.g., 10.00"
              value={costPer1MOutputTokens}
              onChange={(e) => setCostPer1MOutputTokens(e.target.value)}
              disabled={isLoading}
            />
            {/* <div className="flex items-center justify-between pt-2">
              <Label htmlFor="isPremium" className="mb-0">Mark as premium model</Label>
              <Switch
                id="isPremium"
                checked={isPremium}
                onCheckedChange={setIsPremium}
                disabled={isLoading}
              />
            </div> */}
          </div>
          
          <DialogFooter>
            <ModernButton type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </ModernButton>
            <ModernButton type="submit" disabled={isLoading || !modelName.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                isEditMode ? 'Save Changes' : 'Add Model'
              )}
            </ModernButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddModelDialog;
